<template>
  <div class="flex gap-2">
    <input
      v-for="index in 4"
      :key="index"
      :ref="`input-${index - 1}`"
      type="text"
      inputmode="numeric"
      maxlength="1"
      autocomplete="off"
      autocorrect="off"
      autocapitalize="off"
      spellcheck="false"
      :value="digits[index - 1]"
      :class="[
        'pin-input-field h-(--input-height) w-(--input-height) rounded-(--input-radius) border text-center text-2xl',
        digits[index - 1] ? 'border-border-strong bg-surface-sunken' : 'border-border-subtle bg-surface',
        { 'is-error': isError },
      ]"
      @input="onInput(index - 1, $event)"
      @keydown="onKeydown(index - 1, $event)"
      @paste.prevent="onPaste($event)" />
  </div>
</template>

<script lang="ts">
function splitToDigits(value: string): string[] {
  const chars = value.split('')
  return [chars[0] ?? '', chars[1] ?? '', chars[2] ?? '', chars[3] ?? '']
}

export default {
  name: 'PinInput',
  emits: ['update:model-value'],
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    isError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      digits: splitToDigits(this.modelValue),
    }
  },
  watch: {
    modelValue(val: string) {
      this.digits = splitToDigits(val)
    },
  },
  methods: {
    onInput(index: number, event: Event) {
      const input = event.target as HTMLInputElement
      const char = input.value.replace(/\D/g, '').slice(-1)
      this.digits[index] = char
      input.value = char

      if (char && index < 3) {
        const nextInput = (
          this.$refs[`input-${index + 1}`] as HTMLInputElement[]
        )?.[0]
        nextInput?.focus()
      }

      this.$emit('update:model-value', this.digits.join(''))
    },
    onKeydown(index: number, event: KeyboardEvent) {
      if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
        const prevInput = (
          this.$refs[`input-${index - 1}`] as HTMLInputElement[]
        )?.[0]
        prevInput?.focus()
      }
    },
    onPaste(event: ClipboardEvent) {
      const text =
        event.clipboardData?.getData('text').replace(/\D/g, '').slice(0, 4) ??
        ''
      this.digits = [text[0] ?? '', text[1] ?? '', text[2] ?? '', text[3] ?? '']
      this.$emit('update:model-value', this.digits.join(''))
    },

    focus() {
      const firstInput = (this.$refs['input-0'] as HTMLInputElement[])?.[0]
      firstInput?.focus()
    },
  },
  mounted() {
    if (this.autofocus) {
      ;(this.$refs['input-0'] as HTMLInputElement[])?.[0]?.focus()
    }
  },
}
</script>

<style scoped>
@reference "../globals/css/base.css";

/* Visually mask digits as dots without triggering browser password managers */
.pin-input-field {
  -webkit-text-security: disc;
  text-security: disc;
  caret-color: var(--color-action);
}

.pin-input-field:hover {
  @apply border-border-strong;
}

/* Real outline ring (not a box-shadow ring) — matches the focus mechanism used by
   .s-vee-form-field inputs and ComboboxVee, so PIN cells share the same focus token. */
.pin-input-field:focus-visible {
  @apply border-border-focus outline-none;
  outline: 2px solid var(--color-border-focus);
  outline-offset: 1px;
}

/* !important so the error border/text wins over the filled-state utility classes
   regardless of Tailwind's generated rule order — mirrors the DS mockup's is-error rule. */
.pin-input-field.is-error {
  border-color: var(--color-feedback-error) !important;
  color: var(--color-feedback-error);
}
</style>

